import { useState, useRef } from "react";
import { base44 } from "@/api/base44Client";

const ALL_STATUSES = [
  { key: "pending", label: "جديد 📥" },
  { key: "assigned", label: "مُعيَّن 👤" },
  { key: "picked_up", label: "تم الاستلام 🚗" },
  { key: "sorting", label: "فرز 🔍" },
  { key: "processing", label: "تنظيف 🫧" },
  { key: "ready", label: "جاهز ✅" },
  { key: "delivering", label: "في التوصيل 🚚" },
  { key: "delivered", label: "مُسلَّم 🎉" },
  { key: "cancelled", label: "ملغي ❌" },
];

export default function OrderDetail({ order, delegates, laundryServices = [], onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState(order.services || []);
  const [newService, setNewService] = useState({ service_name: "", quantity: 1, unit_price: 0 });
  const [discount, setDiscount] = useState(order.discount || 0);
  const [couponCode, setCouponCode] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(order.payment_method || "");
  const [amountReceived, setAmountReceived] = useState(order.amount_received || 0);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [priceType, setPriceType] = useState("wash_iron"); // "wash_iron" | "wash_only" | "iron_only"
  const [serviceSearch, setServiceSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  const subtotal = services.reduce((s, item) => s + (item.total || 0), 0);

  // Calculate coupon discount with max_discount_amount cap
  const couponDiscount = (() => {
    if (!appliedCoupon) return 0;
    const offer = appliedCoupon._offer;
    if (!offer) return 0;
    if (offer.offer_type === "discount_percent") {
      const raw = Math.round((subtotal * (offer.discount_value || 0)) / 100);
      // Apply cap if max_discount_amount is set
      return offer.max_discount_amount > 0 ? Math.min(raw, offer.max_discount_amount) : raw;
    }
    if (offer.offer_type === "discount_amount") return offer.discount_value || 0;
    if (offer.offer_type === "free_delivery") return 0; // visual only
    return 0;
  })();

  const total = Math.max(0, subtotal - (Number(discount) || 0) - couponDiscount);

  const updateStatus = async (status) => {
    setSaving(true);
    const data = { status };
    if (status === "picked_up") data.picked_up_at = new Date().toISOString();
    if (status === "delivered") data.delivered_at = new Date().toISOString();
    const updated = await base44.entities.LaundryOrder.update(order.id, data);
    onUpdated(updated);
    setSaving(false);
  };

  const assignDelegate = async (delegateId) => {
    const d = delegates.find(x => x.id === delegateId);
    setSaving(true);
    const updated = await base44.entities.LaundryOrder.update(order.id, {
      delegate_id: delegateId,
      delegate_name: d?.name || "",
      status: order.status === "pending" ? "assigned" : order.status,
    });
    onUpdated(updated);
    setSaving(false);
  };

  const selectFromCatalog = (serviceId, pType) => {
    if (!serviceId) return;
    const s = laundryServices.find(x => x.id === serviceId);
    if (!s) return;
    const today = new Date().toISOString().slice(0, 10);
    const onSale = s.sale_price && s.sale_start && s.sale_end && today >= s.sale_start && today <= s.sale_end;
    const type = pType || priceType;
    let price;
    let serviceName = s.name;
    if (type === "iron_only" && s.price_iron_only) {
      price = s.price_iron_only;
      serviceName = `${s.name} (كوي فقط)`;
    } else if (type === "wash_only" && s.price_wash_only) {
      price = s.price_wash_only;
      serviceName = `${s.name} (غسيل فقط)`;
    } else {
      price = onSale ? s.sale_price : s.price;
    }
    setNewService(p => ({ ...p, service_name: serviceName, unit_price: price }));
  };

  const handleServiceSelect = (serviceId) => {
    setSelectedServiceId(serviceId);
    const s = laundryServices.find(x => x.id === serviceId);
    // Reset to wash_iron unless the service has the current priceType
    let newType = priceType;
    if (newType === "iron_only" && !s?.price_iron_only) newType = "wash_iron";
    if (newType === "wash_only" && !s?.price_wash_only) newType = "wash_iron";
    if (newType === "wash_iron" && !s?.price && s?.price_wash_only) newType = "wash_only";
    setPriceType(newType);
    selectFromCatalog(serviceId, newType);
    setServiceSearch(s?.name || "");
    setShowSuggestions(false);
  };

  const filteredServiceSuggestions = serviceSearch.trim()
    ? laundryServices.filter(s =>
        s.name?.includes(serviceSearch) || s.name_en?.toLowerCase().includes(serviceSearch.toLowerCase())
      )
    : laundryServices;

  const handlePriceTypeChange = (type) => {
    setPriceType(type);
    if (selectedServiceId) selectFromCatalog(selectedServiceId, type);
  };

  const addService = () => {
    if (!newService.service_name || newService.unit_price <= 0) return;
    const item = {
      ...newService,
      quantity: Number(newService.quantity),
      unit_price: Number(newService.unit_price),
      total: Number(newService.quantity) * Number(newService.unit_price),
    };
    setServices(prev => [...prev, item]);
    setNewService({ service_name: "", quantity: 1, unit_price: 0 });
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponMsg(null);
    // Find coupon by code + customer phone
    const coupons = await base44.entities.Coupon.filter({ code: couponCode.trim().toUpperCase() });
    if (coupons.length === 0) {
      setCouponMsg({ type: "error", text: "الكوبون غير موجود" });
      return;
    }
    const coupon = coupons[0];
    if (coupon.customer_phone !== order.customer_phone) {
      setCouponMsg({ type: "error", text: "هذا الكوبون لعميل آخر" });
      return;
    }
    if (coupon.status !== "unused") {
      setCouponMsg({ type: "error", text: `الكوبون ${coupon.status === "used" ? "مستخدم مسبقاً" : "غير صالح"}` });
      return;
    }
    if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
      setCouponMsg({ type: "error", text: "انتهت صلاحية الكوبون" });
      return;
    }
    // Load the offer to get discount details
    const offers = await base44.entities.Offer.filter({ id: coupon.offer_id });
    const offer = offers[0] || null;
    if (!offer) {
      setCouponMsg({ type: "error", text: "العرض المرتبط بالكوبون غير موجود" });
      return;
    }
    // Check min invoice amount
    if (offer.min_invoice_amount && subtotal < offer.min_invoice_amount) {
      setCouponMsg({ type: "error", text: `الحد الأدنى للفاتورة ${offer.min_invoice_amount} ر.س` });
      return;
    }
    setAppliedCoupon({ ...coupon, _offer: offer });
    let successText = `✅ تم تطبيق الكوبون: ${offer.title}`;
    if (offer.offer_type === "discount_percent" && offer.max_discount_amount > 0) {
      successText += ` (حد أقصى ${offer.max_discount_amount} ر.س)`;
    }
    setCouponMsg({ type: "success", text: successText });
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponMsg(null);
  };

  const removeService = (idx) => setServices(prev => prev.filter((_, i) => i !== idx));

  const saveInvoice = async () => {
    setSaving(true);
    const totalDiscount = (Number(discount) || 0) + couponDiscount;
    const updated = await base44.entities.LaundryOrder.update(order.id, {
      services,
      subtotal,
      discount: totalDiscount,
      total_amount: total,
      ...(appliedCoupon ? { coupon_code: appliedCoupon.code } : {}),
    });
    // Mark coupon as used
    if (appliedCoupon) {
      await base44.entities.Coupon.update(appliedCoupon.id, {
        status: "used",
        used_at: new Date().toISOString(),
      });
    }
    onUpdated(updated);
    setSaving(false);
  };

  const markInvoiceSent = async () => {
    setSaving(true);
    const updated = await base44.entities.LaundryOrder.update(order.id, {
      invoice_sent: true,
      invoice_sent_at: new Date().toISOString(),
    });
    onUpdated(updated);
    setSaving(false);
  };

  const markPaid = async () => {
    setSaving(true);
    const updated = await base44.entities.LaundryOrder.update(order.id, { paid: true });
    onUpdated(updated);
    setSaving(false);
  };

  const savePayment = async () => {
    if (!paymentMethod) return;
    setSaving(true);
    const updated = await base44.entities.LaundryOrder.update(order.id, {
      payment_method: paymentMethod,
      amount_received: Number(amountReceived),
      paid: Number(amountReceived) >= total,
    });
    onUpdated(updated);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-background rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border px-5 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <p className="text-xs text-muted-foreground">رقم الطلب</p>
            <p className="font-black text-cyan-600 text-lg">{order.order_number}</p>
          </div>
          <button onClick={onClose} className="text-2xl text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer Info */}
          <div className="bg-secondary/50 rounded-2xl p-4 space-y-1">
            <p className="font-black text-foreground">{order.customer_name}</p>
            <p className="text-sm text-muted-foreground">📞 {order.customer_phone}</p>
            <p className="text-sm text-muted-foreground">📍 {order.customer_address}{order.district ? ` — ${order.district}` : ""}</p>
            {order.pickup_time && <p className="text-sm text-muted-foreground">🕐 {order.pickup_time}</p>}
            {order.notes && <p className="text-sm text-muted-foreground">📝 {order.notes}</p>}
            {order.location_url && (
              <a
                href={order.location_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-3 py-1.5 rounded-xl hover:bg-cyan-100 transition-all mt-1"
              >
                🗺️ فتح الموقع على الخريطة
              </a>
            )}
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-bold text-muted-foreground mb-2">تغيير الحالة</p>
            <div className="flex flex-wrap gap-2">
              {ALL_STATUSES.map(s => (
                <button
                  key={s.key}
                  disabled={saving}
                  onClick={() => updateStatus(s.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    order.status === s.key
                      ? "bg-cyan-600 text-white shadow"
                      : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Assign Delegate */}
          <div>
            <p className="text-xs font-bold text-muted-foreground mb-2">تعيين المندوب</p>
            <div className="flex flex-wrap gap-2">
              {delegates.map(d => (
                <button
                  key={d.id}
                  disabled={saving}
                  onClick={() => assignDelegate(d.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    order.delegate_id === d.id
                      ? "bg-blue-600 text-white"
                      : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                  }`}
                >
                  👤 {d.name}
                </button>
              ))}
              {delegates.length === 0 && <p className="text-xs text-muted-foreground">لا يوجد مندوبون — أضف من صفحة المندوبين</p>}
            </div>
          </div>

          {/* Invoice Builder */}
          <div>
            <p className="text-xs font-bold text-muted-foreground mb-2">الخدمات والفاتورة</p>

            {/* Saved services */}
            <div className="space-y-2">
              {services.map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-secondary/40 rounded-xl px-3 py-2 text-sm">
                  <span className="flex-1 text-foreground">{s.service_name}</span>
                  <span className="text-muted-foreground">×{s.quantity}</span>
                  <span className="font-bold text-foreground">{s.total} ر.س</span>
                  <button onClick={() => removeService(i)} className="text-red-400 hover:text-red-600">✕</button>
                </div>
              ))}
            </div>

            {/* Add service row */}
            <div className="mt-3 space-y-2">
              {/* Pick from catalog */}
              {laundryServices.length > 0 && (
                <div className="space-y-2">
                  {/* Service search */}
                  <div className="relative" ref={searchRef}>
                    <div className="relative">
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">🔍</span>
                      <input
                        className="w-full bg-background border border-border rounded-xl pr-8 pl-3 py-2 text-xs outline-none focus:border-cyan-500 text-foreground"
                        placeholder="ابحث عن خدمة..."
                        value={serviceSearch}
                        onChange={e => { setServiceSearch(e.target.value); setShowSuggestions(true); setSelectedServiceId(""); }}
                        onFocus={() => setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      />
                      {serviceSearch && (
                        <button
                          type="button"
                          onClick={() => { setServiceSearch(""); setSelectedServiceId(""); setShowSuggestions(false); }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                        >✕</button>
                      )}
                    </div>
                    {showSuggestions && filteredServiceSuggestions.length > 0 && (
                      <div className="absolute z-50 top-full mt-1 right-0 left-0 bg-card border border-border rounded-xl shadow-xl max-h-48 overflow-y-auto">
                        {filteredServiceSuggestions.map(s => {
                          const today = new Date().toISOString().slice(0, 10);
                          const onSale = s.sale_price && s.sale_start && s.sale_end && today >= s.sale_start && today <= s.sale_end;
                          const price = onSale ? s.sale_price : s.price;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onMouseDown={() => handleServiceSelect(s.id)}
                              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary transition-colors text-right ${selectedServiceId === s.id ? "bg-cyan-50 dark:bg-cyan-900/20" : ""}`}
                            >
                              <span className="font-bold text-foreground">{s.icon} {s.name}</span>
                              <span className="text-cyan-600 dark:text-cyan-400 font-bold mr-2">
                                {price} ر.س {onSale ? "🏷️" : ""}
                              </span>
                            </button>
                          );
                        })}
                        {filteredServiceSuggestions.length === 0 && (
                          <p className="text-xs text-muted-foreground text-center py-3">لا نتائج</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Price type selector — shown only when a service is selected */}
                  {selectedServiceId && (() => {
                    const sel = laundryServices.find(x => x.id === selectedServiceId);
                    return (
                      <div className="flex gap-1.5 flex-wrap">
                        {sel?.price && (
                          <button
                            type="button"
                            onClick={() => handlePriceTypeChange("wash_iron")}
                            className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all border-2 ${priceType === "wash_iron" ? "bg-cyan-600 text-white border-cyan-600" : "bg-secondary text-muted-foreground border-border"}`}
                          >
                            🫧 غسيل + كوي ({sel.price} ر.س)
                          </button>
                        )}
                        {sel?.price_wash_only && (
                          <button
                            type="button"
                            onClick={() => handlePriceTypeChange("wash_only")}
                            className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all border-2 ${
                              priceType === "wash_only" ? "bg-blue-500 text-white border-blue-500" : "bg-secondary text-muted-foreground border-border"
                            }`}
                          >
                            💧 غسيل فقط ({sel.price_wash_only} ر.س)
                          </button>
                        )}
                        {sel?.price_iron_only && (
                          <button
                            type="button"
                            onClick={() => handlePriceTypeChange("iron_only")}
                            className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all border-2 ${
                              priceType === "iron_only" ? "bg-orange-500 text-white border-orange-500" : "bg-secondary text-muted-foreground border-border"
                            }`}
                          >
                            🔥 كوي فقط ({sel.price_iron_only} ر.س)
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Manual inputs */}
              <div className="grid grid-cols-12 gap-2">
                <input
                  className="col-span-5 bg-background border border-border rounded-xl px-3 py-2 text-xs outline-none focus:border-cyan-500"
                  placeholder="اسم الخدمة"
                  value={newService.service_name}
                  onChange={e => setNewService(p => ({ ...p, service_name: e.target.value }))}
                />
                <input
                  type="number"
                  className="col-span-3 bg-background border border-border rounded-xl px-2 py-2 text-xs outline-none focus:border-cyan-500 text-center"
                  placeholder="الكمية"
                  value={newService.quantity}
                  min={1}
                  onChange={e => setNewService(p => ({ ...p, quantity: e.target.value }))}
                />
                <input
                  type="number"
                  className="col-span-3 bg-background border border-border rounded-xl px-2 py-2 text-xs outline-none focus:border-cyan-500 text-center"
                  placeholder="السعر"
                  value={newService.unit_price}
                  min={0}
                  onChange={e => setNewService(p => ({ ...p, unit_price: e.target.value }))}
                />
                <button onClick={addService} className="col-span-1 bg-cyan-600 text-white rounded-xl text-sm font-bold hover:bg-cyan-700">+</button>
              </div>
            </div>

            {/* Manual discount */}
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-muted-foreground">خصم يدوي:</span>
              <input
                type="number"
                value={discount}
                min={0}
                onChange={e => setDiscount(e.target.value)}
                className="w-24 bg-background border border-border rounded-xl px-2 py-1.5 text-xs outline-none focus:border-cyan-500 text-center"
              />
              <span className="text-xs text-muted-foreground">ر.س</span>
            </div>

            {/* Coupon field */}
            <div className="mt-3">
              <p className="text-xs font-bold text-muted-foreground mb-1.5">كوبون العميل</p>
              {appliedCoupon ? (
                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-3 py-2">
                  <span className="flex-1 text-xs font-bold text-green-700 dark:text-green-400">{couponMsg?.text}</span>
                  {appliedCoupon._offer.offer_type === "discount_percent" && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-black">
                      -{couponDiscount} ر.س
                    </span>
                  )}
                  {appliedCoupon._offer.offer_type === "discount_amount" && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-black">
                      -{couponDiscount} ر.س
                    </span>
                  )}
                  {appliedCoupon._offer.offer_type === "free_delivery" && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-black">
                      🚗 توصيل مجاني
                    </span>
                  )}
                  <button onClick={removeCoupon} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    className="flex-1 bg-background border border-border rounded-xl px-3 py-2 text-xs outline-none focus:border-cyan-500 uppercase"
                    placeholder="أدخل كود الكوبون"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    dir="ltr"
                  />
                  <button
                    onClick={applyCoupon}
                    className="bg-amber-500 text-white px-4 rounded-xl text-xs font-bold hover:bg-amber-600 transition-all"
                  >
                    تطبيق
                  </button>
                </div>
              )}
              {couponMsg && couponMsg.type === "error" && (
                <p className="text-xs text-red-500 mt-1">{couponMsg.text}</p>
              )}
            </div>

            {/* Totals summary */}
            <div className="mt-3 bg-secondary/40 rounded-xl px-4 py-3 space-y-1 text-xs">
              <div className="flex justify-between text-muted-foreground">
                <span>المجموع الفرعي</span><span>{subtotal} ر.س</span>
              </div>
              {Number(discount) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>خصم يدوي</span><span>- {discount} ر.س</span>
                </div>
              )}
              {couponDiscount > 0 && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>خصم الكوبون</span><span>- {couponDiscount} ر.س</span>
                </div>
              )}
              {appliedCoupon?._offer?.offer_type === "free_delivery" && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>🚗 توصيل مجاني</span><span>—</span>
                </div>
              )}
              <div className="flex justify-between font-black text-sm text-cyan-600 border-t border-border pt-1">
                <span>الإجمالي</span><span>{total} ر.س</span>
              </div>
            </div>

            <button
              disabled={saving}
              onClick={saveInvoice}
              className="w-full mt-3 bg-cyan-600 text-white font-bold py-2.5 rounded-xl hover:bg-cyan-700 transition-all disabled:opacity-60 text-sm"
            >
              💾 حفظ الفاتورة
            </button>
          </div>

          {/* Payment Method & Amount Received */}
          <div>
            <p className="text-xs font-bold text-muted-foreground mb-2">طريقة الدفع والمبلغ المستلم</p>
            
            {/* Payment Method */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                onClick={() => setPaymentMethod("cash")}
                className={`py-2.5 rounded-xl text-xs font-bold transition-all border-2 ${
                  paymentMethod === "cash"
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-secondary text-muted-foreground border-border hover:bg-secondary/80"
                }`}
              >
                💵 نقدي
              </button>
              <button
                onClick={() => setPaymentMethod("network")}
                className={`py-2.5 rounded-xl text-xs font-bold transition-all border-2 ${
                  paymentMethod === "network"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-secondary text-muted-foreground border-border hover:bg-secondary/80"
                }`}
              >
                🌐 شبكة
              </button>
              <button
                onClick={() => setPaymentMethod("transfer")}
                className={`py-2.5 rounded-xl text-xs font-bold transition-all border-2 ${
                  paymentMethod === "transfer"
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-secondary text-muted-foreground border-border hover:bg-secondary/80"
                }`}
              >
                🔄 تحويل
              </button>
            </div>

            {/* Amount Received */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">المبلغ المستلم:</span>
              <input
                type="number"
                value={amountReceived}
                min={0}
                onChange={e => setAmountReceived(e.target.value)}
                className="flex-1 bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500 text-center font-bold"
                placeholder="0.00"
              />
              <span className="text-xs text-muted-foreground">ر.س</span>
            </div>

            {/* Payment Status */}
            {paymentMethod && (
              <div className={`mt-3 p-3 rounded-xl border-2 ${
                Number(amountReceived) >= total
                  ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700"
                  : "bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700"
              }`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-muted-foreground">حالة الدفع:</span>
                  <span className={`text-xs font-black ${
                    Number(amountReceived) >= total
                      ? "text-green-700 dark:text-green-400"
                      : "text-orange-700 dark:text-orange-400"
                  }`}>
                    {Number(amountReceived) >= total ? "✅ مكتمل" : `⏳ متبقي ${total - Number(amountReceived)} ر.س`}
                  </span>
                </div>
              </div>
            )}

            <button
              disabled={saving || !paymentMethod}
              onClick={savePayment}
              className="w-full mt-3 bg-cyan-600 text-white font-bold py-2.5 rounded-xl hover:bg-cyan-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            >
              💾 حفظ طريقة الدفع
            </button>
          </div>

          {/* Invoice Actions */}
          <div className="flex gap-3">
            <button
              disabled={saving || order.invoice_sent}
              onClick={markInvoiceSent}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all ${
                order.invoice_sent
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 cursor-default"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {order.invoice_sent ? "✅ الفاتورة أُرسلت" : "📧 تأكيد إرسال الفاتورة"}
            </button>
            <button
              disabled={saving || order.paid}
              onClick={markPaid}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all ${
                order.paid
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 cursor-default"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {order.paid ? "✅ مدفوع" : "💵 تأكيد الدفع"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}